<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';

import { IconDelete, IconEdit } from '@/components/design/icons';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import CkEditorInput from '@/components/CkeditorInput.vue';
import HtmlRenderer from '@/components/HtmlRenderer.vue';
import {
  type KanbanCommentInDb,
  type KanbanCommentCreateUser,
  type KanbanCommentUpdate,
  type FilterableSearchQuery,
  searchKanbanComment,
  createKanbanComment,
  updateKanbanComment,
  deleteKanbanComment,
} from '@/api';
import { logger } from '@/utils/logger';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

// Define props
interface Props {
  reviewUid: string;
}

const props = defineProps<Props>();

// State variables
const message = useGaiaToast();

const comments = ref<KanbanCommentInDb[]>([]);
const totalComments = ref<number>(0);
const loading = ref<boolean>(false);
const newComment = ref<string>('');
const searchQuery = ref<string>('');
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const editingCommentId = ref<string | null>(null);
const editingContent = ref<string>('');
const showAddComment = ref<boolean>(false);

// Stores
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();

// Get current user email
const currentUserEmail = computed(() => authStore.sessionEmail);

// User lookup function
function getUserName(email: string): string {
  // Check for AI agent
  if (email === 'agent@gaia.law') {
    return 'Gaia AI Agent';
  }

  // Find user in organization
  const user = organizationStore.users.find(user => user.email === email);
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
  return user?.full_name || user?.email || email;
}

function getUserInitials(email: string): string {
  const name = getUserName(email);
  if (name === 'Gaia AI Agent') return 'AI';

  // Extract initials from name
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${(parts.at(-1) ?? '')[0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

// Format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString();
}

// Check if user is the author of a comment
function isCommentAuthor(comment: KanbanCommentInDb): boolean {
  return comment.author_user_email === currentUserEmail.value;
}

// Fetch comments
async function fetchComments(): Promise<void> {
  loading.value = true;

  try {
    const query: FilterableSearchQuery = {
      text: searchQuery.value,
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
      sort_key: 'created_at',
      sort_direction: 'desc',
    };

    const response = await searchKanbanComment({
      path: { review_uid: props.reviewUid },
      body: query,
    });
    if (!response.data) {
      throw new Error('Failed to fetch comments');
    }
    const result = response.data;

    comments.value = result.results;
    totalComments.value = result.result_count;
  } catch (error) {
    logger.error('Failed to fetch comments:', error);
  } finally {
    loading.value = false;
  }
}

// Toggle add comment section visibility
function toggleAddComment(): void {
  showAddComment.value = !showAddComment.value;
  // When showing the section, reset the comment field
  if (showAddComment.value) {
    newComment.value = '';
  }
}

// After creating a comment, hide the add section
async function createComment(): Promise<void> {
  // Say so rather than returning quietly: an empty body used to make the Post
  // button look broken — no request, no error, nothing on screen.
  if (!newComment.value.trim()) {
    message.warning('Write a comment before posting.');
    return;
  }

  loading.value = true;

  try {
    const commentData: KanbanCommentCreateUser = {
      body: newComment.value,
      review_uid: props.reviewUid,
    };

    await createKanbanComment({ body: commentData });

    newComment.value = '';

    // Hide the add comment section after posting
    showAddComment.value = false;

    // Refresh comments
    await fetchComments();
  } catch (error) {
    logger.error('Failed to create comment:', error);
    message.error('Failed to post the comment.');
  } finally {
    loading.value = false;
  }
}

// Start editing comment
function startEditComment(comment: KanbanCommentInDb): void {
  editingCommentId.value = comment.uid;
  editingContent.value = comment.body;
}

// Cancel editing
function cancelEditComment(): void {
  editingCommentId.value = null;
  editingContent.value = '';
}

// Update comment
async function updateComment(): Promise<void> {
  if (!editingCommentId.value || !editingContent.value.trim()) return;

  loading.value = true;

  try {
    const commentData: KanbanCommentUpdate = {
      body: editingContent.value,
    };

    await updateKanbanComment({
      path: { kanban_comment_uid: editingCommentId.value },
      body: commentData,
    });

    // Reset editing state
    editingCommentId.value = null;
    editingContent.value = '';

    // Refresh comments
    await fetchComments();
  } catch (error) {
    logger.error('Failed to update comment:', error);
  } finally {
    loading.value = false;
  }
}

// Delete comment
async function deleteComment(commentId: string): Promise<void> {
  loading.value = true;

  try {
    await deleteKanbanComment({ path: { kanban_comment_uid: commentId } });

    // Refresh comments
    await fetchComments();
  } catch (error) {
    logger.error('Failed to delete comment:', error);
  } finally {
    loading.value = false;
  }
}

// Search comments
function handleSearch(): void {
  currentPage.value = 1; // Reset to first page
  void fetchComments();
}

// Watch for changes in pagination
watch([currentPage, pageSize], () => {
  void fetchComments();
});

// Watch for evaluation UID changes
watch(
  () => props.reviewUid,
  () => {
    if (props.reviewUid) {
      currentPage.value = 1;
      void fetchComments();
    }
  }
);

// Initial load
onMounted(() => {
  if (props.reviewUid) {
    void fetchComments();
  }

  // Ensure we have organization users
  if (organizationStore.users.length === 0) {
    organizationStore.fetchOrganizationUsers().catch(error => {
      logger.error('Failed to fetch organization users:', error);
    });
  }
});
</script>

<template>
  <div class="comments-section">
    <!-- Search and actions header -->
    <div class="comments-header">
      <h3>Comments ({{ totalComments }})</h3>
      <div class="header-actions">
        <div class="search-container">
          <gaia-search-input
            v-model:value="searchQuery"
            variant="panel"
            size="medium"
            placeholder="Search comments"
            @search="handleSearch"
          />
        </div>
        <NButton type="primary" size="small" @click="toggleAddComment">
          {{ showAddComment ? 'Cancel' : 'Add Comment' }}
        </NButton>
      </div>
    </div>

    <!-- Comment input area - conditionally shown -->
    <div v-if="showAddComment" class="add-comment-section">
      <div class="editor-container">
        <CkEditorInput
          v-model="newComment"
          extended-mode
          placeholder="Write a comment..."
          :initial-styling="'paragraph'"
          class="compact-editor"
        />
      </div>
      <div class="edit-actions-container">
        <div class="comment-actions">
          <NButton type="primary" :loading="loading" @click="createComment"> Post Comment </NButton>
        </div>
      </div>
    </div>

    <NDivider />

    <!-- Loading state -->
    <NSpin :show="loading">
      <div class="comments-list">
        <!-- Empty state -->
        <NEmpty v-if="comments.length === 0 && !loading" description="No comments found" />

        <!-- Comments list -->
        <div v-for="comment in comments" :key="comment.uid" class="comment-item">
          <div class="comment-header">
            <div class="comment-author">
              <NAvatar :size="32" round>{{ getUserInitials(comment.author_user_email) }}</NAvatar>
              <div class="author-info">
                <div class="author-name">{{ getUserName(comment.author_user_email) }}</div>
                <div class="comment-date">{{ formatDate(comment.created_at) }}</div>
              </div>
            </div>

            <!-- Comment actions for author -->
            <div v-if="isCommentAuthor(comment)" class="comment-actions">
              <NButton text @click="startEditComment(comment)">
                <NIcon>
                  <IconEdit />
                </NIcon>
              </NButton>
              <NPopconfirm
                positive-text="Yes"
                negative-text="No"
                @positive-click="deleteComment(comment.uid)"
              >
                <template #trigger>
                  <NButton text>
                    <NIcon>
                      <IconDelete />
                    </NIcon>
                  </NButton>
                </template>
                Are you sure you want to delete this comment?
              </NPopconfirm>
            </div>
          </div>

          <!-- Comment content (normal view) -->
          <div v-if="editingCommentId !== comment.uid" class="comment-content">
            <HtmlRenderer :content="comment.body" />
          </div>

          <!-- Comment content (edit view) -->
          <div v-else class="comment-edit">
            <CkEditorInput
              v-model="editingContent"
              extended-mode
              :initial-styling="'paragraph'"
              class="compact-editor"
            />
            <div class="edit-actions-container">
              <div class="edit-actions">
                <NButton type="primary" :loading="loading" @click="updateComment"> Save </NButton>
                <NButton @click="cancelEditComment"> Cancel </NButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NSpin>

    <!-- Pagination -->
    <div v-if="totalComments > pageSize" class="pagination-container">
      <gaia-pagination
        v-model:page="currentPage"
        v-model:page-size="pageSize"
        :total="totalComments"
        :page-sizes="[10, 20, 50]"
      />
    </div>
  </div>
</template>

<style scoped>
/* Grows with its content: this now lives in the review details drawer, whose
   body is the scroller. The old `height: 100%` + nested `overflow: auto` came
   from the fixed-height ticket modal, and in an auto-height flex column there is
   nothing for the percentage to resolve against — the list collapsed and
   scrolled its rows out of view. */
.comments-section {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.comments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.comments-header h3 {
  margin: 0;
  font-size: 16px;
}

.search-container {
  width: 240px;
}

.add-comment-section {
  margin-bottom: 16px;
}

.editor-container {
  border-radius: 4px;
}

.editor-container :deep(.ck-editor__editable_inline),
.comment-edit :deep(.ck-editor__editable_inline) {
  min-height: 120px !important;
  max-height: 120px !important;
  height: 120px !important;
  overflow-y: auto !important;
}

/* Target the CkEditor wrapper elements */
.editor-container :deep(.ck.ck-editor),
.comment-edit :deep(.ck.ck-editor) {
  width: 100% !important;
}

.editor-container :deep(.ck-editor__main),
.comment-edit :deep(.ck-editor__main) {
  height: auto !important;
  width: 100% !important;
}

/* Make sure the content area renders properly */
.editor-container :deep(.ck-content),
.comment-edit :deep(.ck-content) {
  width: 100% !important;
  height: 120px !important;
  min-height: 120px !important;
}

/* Add a class to the CkEditorInput component */
.compact-editor {
  height: auto !important;
}

.comment-actions {
  display: flex;
  gap: 8px;
}

.comments-list {
  margin-bottom: 16px;
}

.comment-item {
  padding: 16px;
  margin-bottom: 20px;
  border-radius: 4px;
  background-color: #f9f9f9;
  border: 1px solid #eaeaea;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.comment-author {
  display: flex;
  align-items: center;
}

.author-info {
  margin-left: 8px;
}

.author-name {
  font-weight: 500;
  font-size: 14px;
}

.comment-date {
  font-size: 12px;
  color: #999;
}

.comment-content {
  font-size: 14px;
  line-height: 1.5;
}

/* Ensure proper rendering of formatted text in comment content */
.comment-content :deep(strong),
.comment-content :deep(b) {
  font-weight: bold !important;
}

.comment-content :deep(em),
.comment-content :deep(i) {
  font-style: italic !important;
}

.comment-content :deep(u) {
  text-decoration: underline !important;
}

/* Add proper styling for CkEditor content */
.editor-container :deep(.ck-content strong),
.editor-container :deep(.ck-content b),
.comment-edit :deep(.ck-content strong),
.comment-edit :deep(.ck-content b) {
  font-weight: bold !important;
}

.editor-container :deep(.ck-content em),
.editor-container :deep(.ck-content i),
.comment-edit :deep(.ck-content em),
.comment-edit :deep(.ck-content i) {
  font-style: italic !important;
}

.comment-edit {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
}

.edit-actions-container {
  padding-top: 10px;
  clear: both;
  width: 100%;
  margin-bottom: 10px;
}

.edit-actions {
  display: flex;
  gap: 8px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
